/**
 * Показ ошибок валидации: нормализация сырого аргумента (`validator.errors()`, `ModelState`) и одно окно
 * SweetAlert2 со всеми текстами; подавление повторного идентичного набора и безопасный вызов для `validate()`.
 */

export interface ShowValidationErrorsOptions {
    /** Заголовок окна; по умолчанию — локализованный `attention` из `window.getLocaleDict()`. */
    title?: string;
    /** Только подтвердить наличие ошибок, окно не показывать. */
    silent?: boolean;
}

export interface NormalizedValidationErrors {
    messages: string[];
    fields: string[];
}

interface RawValidationError {
    field: string;
    message: string;
}

const POPUP_REPEAT_SUPPRESSION_MS = 800;

let lastPopupSignature = '';
let lastPopupTime = 0;

/**
 * Рекурсивно собирает пары «поле — текст ошибки» из всех форм, которые приходят в приложение: `string[]`
 * (`validator.errors()`), `Record<string, unknown>` (server `ModelState`, путь клеится через точку), `string`
 * и пустые структуры. Дедупликация — в `normalizeValidationErrors`.
 */
function collectValidationErrors(value: unknown, path: string, sink: RawValidationError[]): void {
    if (value === null || value === undefined) {
        return;
    }

    if (typeof value === 'string') {
        sink.push({ field: path, message: value });
    }
    else if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        sink.push({ field: path, message: String(value) });
    }
    else if (Array.isArray(value)) {
        for (const item of value) {
            collectValidationErrors(item, path, sink);
        }
    }
    else if (typeof value === 'object') {
        for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
            collectValidationErrors(nested, path ? `${path}.${key}` : key, sink);
        }
    }
}

/**
 * Плоский список без дубликатов по тексту с нормализованными пробелами: server `ModelState` отдаёт один
 * и тот же текст под разными ключами; порядок добавления сохраняется.
 */
export function normalizeValidationErrors(errors: unknown): NormalizedValidationErrors {
    const collected: RawValidationError[] = [];
    const messages: string[] = [];
    const fields: string[] = [];
    const seenMessages = new Set<string>();
    const seenFields = new Set<string>();

    collectValidationErrors(errors, '', collected);

    for (const { field, message } of collected) {
        const deduplicationKey = message.replace(/\s+/g, ' ').trim();

        if (!deduplicationKey) {
            continue;
        }

        if (!seenMessages.has(deduplicationKey)) {
            seenMessages.add(deduplicationKey);
            messages.push(message);
        }

        if (field && !seenFields.has(field)) {
            seenFields.add(field);
            fields.push(field);
        }
    }

    return { messages, fields };
}

/**
 * Одно окно со всеми ошибками (тексты склеиваются через `<br>` в `html`).
 * `true` — ошибки ПРИНЯТЫ к показу: `silent`, подавление повторного идентичного набора за
 * `POPUP_REPEAT_SUPPRESSION_MS` (800 мс) или отправка в `fire()`; «показано» от «подавлено» не отличить.
 * Уже открытое окно ошибки НЕ подавляет: показанное ранее окно закрывается вызовом `window.swal.close()`
 * непосредственно перед `fire()`, это замена, а не стопка, и ошибки валидации важнее чужого окна.
 * Отдельного закрывателя у модуля нет, а `close()` без открытого окна безопасен: закрывать нечего —
 * это no-op. Закрытие выполняется ДО `fire()`, потому что `_destroy()` не завершает промис уничтоженного
 * экземпляра — `await window.swal.fire(...)` повис бы.
 */
export function showValidationErrors(errors: unknown, options?: ShowValidationErrorsOptions): boolean {
    const { messages } = normalizeValidationErrors(errors);

    if (!messages.length) {
        return false;
    }

    if (options?.silent) {
        return true;
    }

    const signature = JSON.stringify(messages);
    const now = Date.now();

    if (signature === lastPopupSignature && now - lastPopupTime < POPUP_REPEAT_SUPPRESSION_MS) {
        return true;
    }

    lastPopupSignature = signature;
    lastPopupTime = now;

    window.swal.close();

    /** Заголовок окна — локализованный `attention` из словаря текущей культуры, если `options.title` не задан. */
    void window.swal.fire({
        titleText: options?.title ?? window.getLocaleDict().attention,
        html: messages.join('<br>'),
        icon: 'warning'
    });

    return true;
}

/**
 * Показывает ошибки, не позволяя сбою отображения сломать валидацию: `validate()` обязан вернуть синхронный
 * `boolean`, поэтому провал презентации (нет SweetAlert2, исключение внутри `fire`) не меняет результат
 * и не утекает наружу. Обёрнут ТОЛЬКО свой побочный эффект, так что контракт оригинальных методов сохранён;
 * экспортируется и для серверного канала (`window.checkValidationErrors`).
 */
export function showValidationErrorsSafely(errors: unknown): void {
    try {
        showValidationErrors(errors);
    }
    catch (error) {
        console.warn('[validation-popup] ошибки валидации не показаны: сбой отображения окна.', error);
    }
}
